<script setup lang="ts">
import { useHead } from "@unhead/vue";
import type { Locale } from "shared-core/services/i18n.ts";
import WebsiteFooter from "shared-ui/components/layout/WebsiteFooter.vue";
import WebsiteHeader from "shared-ui/components/layout/WebsiteHeader.vue";
import { computed, provide, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { RouterView, useRoute, useRouter } from "vue-router";
import { type LayoutContext, LayoutKey } from "@/types/layout-key.ts";
import { useHrefLang } from "@/utils/seo-utils.ts";
import { useSocialImage } from "@/utils/social-images.ts";

const route = useRoute();
const router = useRouter();

const { locale } = useI18n();

// Set <html lang> per route render
useHead(() => ({ htmlAttrs: { lang: locale.value } }));
useHrefLang(route, locale.value as Locale);
useSocialImage(route, locale);

const layout = reactive({
  hideMenuItems: false,
  set(opts: Partial<LayoutContext>) {
    Object.assign(layout, opts);
  },
});
provide(LayoutKey, layout);

// The beige page background is declared per-route via `meta.beige`, so it stays
// consistent during navigation instead of racing page setup against unmount.
const beigeBackground = computed(() => route.meta.beige === true);
</script>

<template>
  <div :class="{ 'bg-brand-beige': beigeBackground }">
    <WebsiteHeader
      :security-link="
        router.resolve({
          name: 'trust-center-locale',
          params: { locale: locale },
        }).href
      "
      :hide-menu-items="layout.hideMenuItems"
      :home-link="
        router.resolve({
          name: 'home-locale',
          params: { locale: locale },
        }).href
      "
      :product-link="
        router.resolve({
          name: 'product-locale',
          params: { locale: locale },
        }).href
      "
      :pricing-link="
        router.resolve({
          name: 'pricing-locale',
          params: { locale: locale },
        }).href
      "
      :partners-link="
        router.resolve({
          name: 'partners-locale',
          params: { locale: locale },
        }).href
      "
      :company-link="
        router.resolve({
          name: 'company-locale',
          params: { locale: locale },
        }).href
      "
    >
      <template #cta-desktop>
        <SwitchLanguage />
      </template>
      <template #cta-mobile>
        <SwitchLanguage />
      </template>
    </WebsiteHeader>
    <main class="min-h-screen">
      <!-- key forces a fresh render per route during SSG -->
      <RouterView :key="route.fullPath" />
    </main>
    <WebsiteFooter
      :product-link="
        router.resolve({
          name: 'product-locale',
          params: { locale: locale },
        }).href
      "
      :pricing-link="
        router.resolve({
          name: 'pricing-locale',
          params: { locale: locale },
        }).href
      "
      :resource-link="
        router.resolve({
          name: 'resources-locale',
          params: { locale: locale },
        }).href
      "
      :partners-link="
        router.resolve({
          name: 'partners-locale',
          params: { locale: locale },
        }).href
      "
      :company-link="
        router.resolve({
          name: 'company-locale',
          params: { locale: locale },
        }).href
      "
      :agent-download-link="
        router.resolve({
          name: 'download-app-locale',
          params: { locale: locale },
        }).href
      "
      :security-link="
        router.resolve({
          name: 'trust-center-locale',
          params: { locale: locale },
        }).href
      "
      :privacy-policy-link="
        router.resolve({
          name: 'privacy-policy-locale',
          params: { locale: locale },
        }).href
      "
      :terms-and-conditions-link="
        router.resolve({
          name: 'terms-and-conditions-locale',
          params: { locale: locale },
        }).href
      "
    />
  </div>
</template>

<style scoped lang="css"></style>
